import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/license.css";

function License() {
  const navigate = useNavigate();

  const [license, setLicense] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);

  const baseURL = sessionStorage.getItem("baseURL");

  /* =========================
     AUTO ROW CALCULATION
  ========================= */
  useEffect(() => {
    const calculateRows = () => {
      const viewportHeight = window.innerHeight;
      const usableHeight = viewportHeight - 280 - 60 - 50 - 80;
      const rows = Math.floor(usableHeight / 52);
      setRowsPerPage(rows >= 2 ? rows : 2);
    };
    calculateRows();
    window.addEventListener("resize", calculateRows);
    return () => window.removeEventListener("resize", calculateRows);
  }, []);

  /* =========================
     AUTH + FETCH
  ========================= */
  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") {
      navigate("/");
    } else if (sessionStorage.getItem("isAdmin") !== "true") {
      navigate("/dashboard");
    }
    fetchDetails();
  }, [navigate]);

  const fetchDetails = async () => {
    const res = await axios.get(baseURL + "license/alllicense");
    const map = {};
    res.data.forEach((lic) => {
      if (!map[lic.sku_id]) map[lic.sku_id] = [];
      map[lic.sku_id].push(lic);
    });
    Object.values(map).forEach((g) =>
      g.sort((a, b) => new Date(b.end_date) - new Date(a.end_date))
    );
    const sortedEntries = Object.entries(map).sort(
      ([, aL], [, bL]) => {
        const pa = classifySkuGroup(aL);
        const pb = classifySkuGroup(bL);
        if (pa !== pb) return pa - pb;
        return new Date(aL[0].end_date) - new Date(bL[0].end_date);
      }
    );
    setLicense(Object.fromEntries(sortedEntries));
  };

  const classifySkuGroup = (licenses) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let expired = 0, active = 0, upcoming = 0, minDaysLeft = Infinity;
    licenses.forEach((l) => {
      const start = new Date(l.start_date); start.setHours(0,0,0,0);
      const end   = new Date(l.end_date);   end.setHours(0,0,0,0);
      if (end < today) expired++;
      else if (start > today) upcoming++;
      else {
        active++;
        minDaysLeft = Math.min(minDaysLeft, Math.ceil((end - today) / 86400000));
      }
    });
    if (expired === licenses.length) return 1;
    if (active === 1 && minDaysLeft <= 30) return 2;
    if (active > 0) return 3;
    if (upcoming === licenses.length) return 4;
    return 5;
  };

  /* =========================
     SEARCH FILTER
  ========================= */
  const filteredGroups = Object.entries(license).filter(([, licenses]) =>
    licenses.some((l) =>
      l.sku_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  /* =========================
     PAGINATION
  ========================= */
  const pages = [];
  let current = [], rowCount = 0;
  filteredGroups.forEach(([groupName, licenses]) => {
    const visibleLicenses = expandedGroups[groupName] ? licenses : licenses.slice(0, 2);
    const groupRows = visibleLicenses.length;
    if (rowCount + groupRows > rowsPerPage && current.length > 0) {
      pages.push(current);
      current = [];
      rowCount = 0;
    }
    current.push({ groupName, licenses: visibleLicenses });
    rowCount += groupRows;
  });
  if (current.length) pages.push(current);

  const pageData   = pages[currentPage] || [];
  const totalPages = pages.length;

  useEffect(() => { setCurrentPage(0); }, [searchTerm, expandedGroups, rowsPerPage]);

  /* =========================
     HELPERS
  ========================= */
  const daysLeft = (d) => {
    const t = new Date(); t.setHours(0,0,0,0);
    const e = new Date(d); e.setHours(0,0,0,0);
    return Math.max(Math.ceil((e - t) / 86400000), 0);
  };

  const toggleGroup = (g) =>
    setExpandedGroups((p) => ({ ...p, [g]: !p[g] }));

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="license">

      {/* ── Header ── */}
      <div className="dashboard-header">
        <button className="action-button" onClick={() => navigate("/dashboard")}>
          ← Back
        </button>
      </div>

      <div className="license-container">

        {/* ── Search ── */}
        <div className="table-controls">
          <input
            placeholder="Search by product name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* ── Table ── */}
        <table className="license-table">
          <thead>
            <tr>
              <th colSpan={5} style={{ textAlign: "center" }}>
                License Information
              </th>
            </tr>
            <tr>
              <th>Sl. No.</th>
              <th>Product</th>
              <th>Activation Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {pageData.map(({ groupName, licenses }, gi) =>
              licenses.map((lic, li) => {
                const d = daysLeft(lic.end_date);
                const s = daysLeft(lic.start_date);
                const pillClass =
                  d === 0 ? "expired" :
                  s > 0   ? "upcoming" :
                  d <= 30 ? "warning"  : "valid";
                const pillLabel =
                  d === 0 ? "Expired" :
                  s > 0   ? "Yet to activate" :
                  `${d} day${d === 1 ? "" : "s"} left`;

                return (
                  <tr key={`${groupName}-${li}`}>
                    {li === 0 && (
                      <>
                        <td rowSpan={licenses.length}>
                          {filteredGroups.findIndex(([g]) => g === groupName) + 1}
                        </td>
                        <td rowSpan={licenses.length}>
                          <div className="product-name">
                            {lic.sku_name}
                            {license[groupName].length > 2 && (
                              <button
                                className="toggle-btn"
                                onClick={() => toggleGroup(groupName)}
                              >
                                {expandedGroups[groupName]
                                  ? "View less"
                                  : `+${license[groupName].length - 2} more`}
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                    <td>{new Date(lic.start_date).toLocaleDateString()}</td>
                    <td>{new Date(lic.end_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-pill ${pillClass}`}>
                        {pillLabel}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ── Pagination ── */}
        <div className="pagination-controls">
          <button
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ← Previous
          </button>
          <span>Page {currentPage + 1} of {totalPages || 1}</span>
          <button
            disabled={currentPage + 1 >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

export default License;